import { Spacing, Text, Title } from '@vkontakte/vkui';
import { ReactComponent as DocxIcon } from 'img/filetype/newest/light/document_text_outline_20.svg';
import { ReactComponent as PresentationIcon } from 'img/filetype/newest/light/file_presentations_outline_20.svg';
import { ReactComponent as TableIcon } from 'img/filetype/newest/light/file_table_outline_20.svg';
import React, { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toolbarActions } from 'reactApp/appHelpers/toolbarActions';
import { createAlbumRequest } from 'reactApp/modules/albums/albums.actions';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { EActions } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { FileType } from 'reactApp/types/FileType';
import { useAddActions } from 'reactApp/ui/Toolbar/useAddActions';
import { AddIcon, AlbumIcon, Icon20FolderOutline } from 'reactApp/ui/VKUIIcons';
import { noop } from 'reactApp/utils/helpers';

import styles from './WelcomeNewPromo.css';

const allItems = {
    [EActions.createFolder]: {
        id: EActions.createFolder,
        title: 'Папку',
        icon: <Icon20FolderOutline />,
        onClick: () => toolbarActions.create(FileType.FOLDER, 'welcome'),
    },
    [EActions.createAlbum]: {
        id: EActions.createAlbum,
        title: 'Альбом',
        icon: <AlbumIcon />,
        onClick: noop,
    },
    [EActions.createDoc]: {
        id: EActions.createDoc,
        title: 'Документ',
        icon: <DocxIcon />,
        onClick: () => toolbarActions.create(FileType.DOCX, 'welcome'),
    },
    [EActions.createCell]: {
        id: EActions.createCell,
        title: 'Таблицу',
        icon: <TableIcon />,
        onClick: () => toolbarActions.create(FileType.XLSX, 'welcome'),
    },
    [EActions.createPpt]: {
        id: EActions.createPpt,
        title: 'Презентацию',
        icon: <PresentationIcon />,
        onClick: () => toolbarActions.create(FileType.PPTX, 'welcome'),
    },
};

export const CreateBlock = memo(() => {
    const dispatch = useDispatch();
    const isB2BUser = useSelector(UserSelectors.isB2BUser);
    const storage = useSelector(getCurrentStorage);
    const actions = useAddActions({ storage });
    const flatActions = actions.flatMap((action) => action?.list ?? []);

    const canCreateFiles = flatActions.some(({ id }) => {
        return [EActions.createDoc, EActions.createCell, EActions.createPpt].includes(id as EActions);
    });
    const description = isB2BUser
        ? `Создавайте папки${canCreateFiles ? ', документы, таблицы' : ''} и делитесь с коллегами`
        : 'Создавайте папки, документы, таблицы и делитесь с друзьями';

    const handleCreateAlbum = useCallback(() => {
        dispatch(createAlbumRequest());
    }, []);

    const items = Object.values(allItems).filter(({ id }) => {
        if (isB2BUser && !flatActions.some((action) => action?.id === id)) {
            return;
        }

        if (id === EActions.createAlbum) {
            return !isB2BUser;
        }

        if (id === EActions.createPpt) {
            return isB2BUser;
        }

        return true;
    });

    return (
        <div className={styles.block} data-qa-id="create">
            <div className={styles.icon}>
                <AddIcon />
            </div>
            <Spacing size={24} style={{ pointerEvents: 'none' }} />
            <Title className={styles.block_title} level="3">
                Создать
            </Title>
            <Spacing size={8} style={{ pointerEvents: 'none' }} />
            <Text className={styles.block_text}>{description}</Text>
            <Spacing size={24} style={{ pointerEvents: 'none' }} />
            <div className={styles.items}>
                {items.map((item) => (
                    <div
                        key={item.id}
                        className={styles.item}
                        onClick={item.id === EActions.createAlbum ? handleCreateAlbum : item.onClick}
                        data-qa-id={item.id}
                    >
                        {item.icon}
                        <Spacing size={8} />
                        <Text>{item.title}</Text>
                    </div>
                ))}
            </div>
        </div>
    );
});

CreateBlock.displayName = 'CreateBlock';
